<template>
  <div
    class="blog-container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20"
  >
    <!-- Breadcrumb -->
    <nav class="mb-12">
      <router-link to="/" class="back-link">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
            clip-rule="evenodd"
          />
        </svg>
        Back to home page
      </router-link>
    </nav>

    <!-- Header -->
    <div class="mb-16 max-w-3xl mx-auto text-center">
      <h1 class="text-[42px] font-bold text-gray-900 mb-6">Blog</h1>
      <p class="text-xl text-gray-500 leading-relaxed">
        Insights and updates on anything and everything analytics
      </p>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-12">
      <p class="text-xl text-gray-600">Loading posts...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-12">
      <p class="text-xl text-red-600">{{ error }}</p>
    </div>

    <!-- Blog Posts Grid -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-8">
      <article
        v-for="post in blogPosts"
        :key="post.slug"
        class="group bg-white rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300 border border-gray-100"
      >
        <router-link
          :to="{ name: 'BlogPost', params: { slug: post.slug } }"
          class="block h-full"
        >
          <div class="flex flex-col h-full">
            <!-- Image Container -->
            <div
              class="relative aspect-[16/9] overflow-hidden bg-gray-100 shrink-0"
            >
              <img
                v-if="post.image"
                :src="post.image"
                :alt="post.title"
                class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
            </div>

            <!-- Content Wrapper -->
            <div class="flex flex-col flex-1">
              <!-- Content -->
              <div class="flex-1 p-6 sm:p-8">
                <!-- Tags -->
                <div class="flex flex-wrap gap-2 mb-4">
                  <span
                    v-for="tag in post.tags"
                    :key="tag"
                    :class="[
                      'inline-flex items-center px-3 py-1 rounded-full text-sm transition-colors',
                      getTagStyle(tag).bg,
                      getTagStyle(tag).text,
                      getTagStyle(tag).hover,
                    ]"
                  >
                    {{ tag }}
                  </span>
                </div>

                <!-- Title -->
                <h2
                  class="text-2xl sm:text-3xl font-semibold text-gray-900 mb-4 group-hover:text-primary-green transition-colors leading-tight"
                >
                  {{ post.title }}
                </h2>

                <!-- Excerpt -->
                <p class="text-[18px] text-gray-600 leading-relaxed">
                  {{ post.excerpt }}
                </p>
              </div>

              <!-- Footer -->
              <div class="px-6 sm:px-8 py-4 border-t border-gray-100 mt-auto">
                <div class="flex items-center justify-between">
                  <div class="text-sm sm:text-base text-gray-500">
                    {{ post.date }}
                  </div>
                  <span class="text-sm sm:text-base text-gray-500"
                    >{{ post.readingTime }} min read(s)</span
                  >
                </div>
              </div>
            </div>
          </div>
        </router-link>
      </article>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";

// Tag color mapping system
const tagStyles = {
  Figma: {
    bg: "bg-purple-50",
    text: "text-purple-700",
    hover: "hover:bg-purple-100",
  },
  Animation: {
    bg: "bg-blue-50",
    text: "text-blue-700",
    hover: "hover:bg-blue-100",
  },
  Tutorial: {
    bg: "bg-green-50",
    text: "text-green-700",
    hover: "hover:bg-green-100",
  },
  Design: {
    bg: "bg-pink-50",
    text: "text-pink-700",
    hover: "hover:bg-pink-100",
  },
  Tools: {
    bg: "bg-orange-50",
    text: "text-orange-700",
    hover: "hover:bg-orange-100",
  },
  Comparison: {
    bg: "bg-yellow-50",
    text: "text-yellow-700",
    hover: "hover:bg-yellow-100",
  },
};

// Helper function to get tag styles
const getTagStyle = (tag) => {
  return (
    tagStyles[tag] || {
      bg: "bg-gray-50",
      text: "text-gray-700",
      hover: "hover:bg-gray-100",
    }
  );
};

const blogPosts = ref([
  {
    slug: "complete-ga4-system-for-tracking-all-ai-traffic",
    title: "How to Segment LLM Traffic in GA4: A Complete Guide",
    excerpt:
      "Learn how to track and analyze AI-powered traffic from ChatGPT, Claude, and Perplexity in Google Analytics 4. Discover three proven methods to segment your LLM traffic and gain competitive insights.",
    date: "August 2025",
    readingTime: "24",
    tags: ["GA4", "AI", "Analytics", "Tutorial"],
    image: "/assets/blog/images-post-1/AI-Traffic--Thumbnail-large.webp",
    author: {
      name: "Tony Rositano",
      avatar: null,
    },
  },
  // {
  //   slug: "complete-ga4-system-for-tracking-all-ai-traffic",
  //   title: "The Complete GA4 System For Tracking All AI Traffic",
  //   excerpt:
  //     "- In just a few minutes we'll create 3 ways you can visualize your AI traffic in Google Analytics 4.",
  //   date: "Aug 27, 2025",
  //   readingTime: "8",
  //   tags: ["GA4", "Ai", "Tutorial", "Youtube"],
  //   image:
  //     "https://images.unsplash.com/photo-1626785774573-4b799315345d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80",
  //   author: {
  //     name: "Tony Rositano",
  //     avatar: null,
  //   },
  // },
  // {
  //   slug: "welcome-to-better-insights",
  //   title: "Fable Alternatives | Online Animation Tools",
  //   excerpt:
  //     "Alternatives to Fable.app. A list of animation tools for creating lottie and mp4 files.",
  //   date: "Oct 15, 2024",
  //   readingTime: "5",
  //   tags: ["Tools", "Animation", "Comparison"],
  //   image:
  //     "https://images.unsplash.com/photo-1626785774625-ddcddc3445e9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80",
  //   author: {
  //     name: "Mike Wilson",
  //     avatar:
  //       "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=256&q=80",
  //   },
  // },
]);

const isLoading = ref(false);
const error = ref(null);

// Comment out or remove the onMounted section since we're using static data for now
// onMounted(async () => {
//   try {
//     blogPosts.value = await getAllPosts();
//   } catch (e) {
//     error.value = "Failed to load blog posts. Please try again later.";
//     console.error("Error loading blog posts:", e);
//   } finally {
//     isLoading.value = false;
//   }
// });
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700&display=swap");

.blog-container {
  min-height: calc(100vh - 4rem);
  font-family: "Nunito", sans-serif;
}

/* Optional: Add a subtle gradient background */
.blog-container::before {
  content: "";
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom right,
    rgba(249, 250, 251, 0.8),
    rgba(255, 255, 255, 0.8)
  );
  z-index: -1;
}

/* Back link styling to match BlogPostPage */
.back-link {
  display: inline-flex !important;
  align-items: center !important;
  gap: 0.5rem !important;
  color: #22c55e !important; /* Green color to match TOC active state */
  text-decoration: none !important;
  font-weight: 500 !important;
  font-size: 1.125rem !important; /* text-lg equivalent */
  transition: color 0.2s ease !important;
}

.back-link:hover {
  color: #15803d !important; /* Darker green on hover */
}

.back-link svg {
  width: 20px !important;
  height: 20px !important;
  transition: transform 0.2s ease !important;
}

.back-link:hover svg {
  transform: translateX(-4px) !important;
}

.text-gray-600 {
  font-family: "Nunito", sans-serif;
  font-weight: 400;
}
</style>
